/**
 * Shared CRM attachment helpers — validation, type detection, download.
 * Keep in sync with backend multer limits (5MB + allowlist).
 */

export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

export const ATTACHMENT_ACCEPT =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain";

const ALLOWED_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "txt",
]);

const ALLOWED_MIME = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
]);

export const getFileExtension = (name = "") => {
  const parts = String(name).split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const isImageFile = (doc) => {
  const type = String(doc?.fileType || doc?.type || "").toLowerCase();
  const name = String(doc?.fileName || doc?.name || "").toLowerCase();
  return type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
};

export const isPdfFile = (doc) => {
  const type = String(doc?.fileType || doc?.type || "").toLowerCase();
  const name = String(doc?.fileName || doc?.name || "").toLowerCase();
  return type.includes("pdf") || name.endsWith(".pdf");
};

export const isTextFile = (doc) => {
  const type = String(doc?.fileType || "").toLowerCase();
  const name = String(doc?.fileName || "").toLowerCase();
  return type.startsWith("text/") || name.endsWith(".txt") || name.endsWith(".csv");
};

export const isOfficeFile = (doc) => {
  const type = String(doc?.fileType || "").toLowerCase();
  const name = String(doc?.fileName || "").toLowerCase();
  return (
    /\.(docx?|xlsx?|pptx?)$/i.test(name) ||
    type.includes("officedocument") ||
    type.includes("msword") ||
    type.includes("ms-excel") ||
    type.includes("ms-powerpoint")
  );
};

export const canInlinePreview = (doc) =>
  isImageFile(doc) || isPdfFile(doc) || isTextFile(doc);

/**
 * Validate a browser File before upload. Returns error string or null.
 */
export const validateAttachmentFile = (file) => {
  if (!file) return "Please choose a file";
  if (!file.size || file.size <= 0) return "File is empty";
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return "File too large. Maximum size is 5MB.";
  }
  const ext = getFileExtension(file.name);
  const mime = String(file.type || "").toLowerCase();
  const mimeOk = mime.startsWith("image/") || ALLOWED_MIME.has(mime);
  const extOk = ALLOWED_EXTENSIONS.has(ext);
  if (!mimeOk && !extOk) {
    return "Unsupported file type. Allowed: images, PDF, Word, Excel, TXT.";
  }
  return null;
};

/**
 * Trigger a browser download for a blob URL.
 */
export const triggerBlobDownload = (blobUrl, fileName = "download") => {
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Open blob in a new tab (useful for PDFs/images when user wants external view).
 */
export const openBlobInNewTab = (blobUrl) => {
  window.open(blobUrl, "_blank", "noopener,noreferrer");
};
